import React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { API, StatusPill, na, pct, time } from "@/components/fbShared";

const POS = ["PG", "SG", "SF", "PF", "C"];
const cell = (w, p) => (w && w.opp_first ? `${w.counts[p] || 0} · ${pct(w.share[p] || 0, 0)}` : "N/A");

export const WeaknessMatrix = ({ date }) => {
  const [d, setD] = React.useState(null);
  const [win, setWin] = React.useState("L20");
  React.useEffect(() => { axios.get(`${API}/weakness`, { params: { date } }).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [date]);
  if (!d) return <Loading />;
  if (d.error) return <p className="font-mono text-xs text-rose-400">Weakness matrix unavailable.</p>;
  const lg = d.league.pos_share;
  return (
    <div className="rounded-xl border border-slate-800 bg-[#0B0F17] p-3" data-testid="fb-weakness">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <p className="mr-2 font-display text-[13px] font-bold uppercase text-orange-200">First baskets allowed by position</p>
        {["L20", "L10", "L5", "season", "prev", "home", "away", "index"].map((k) => (
          <button key={k} type="button" onClick={() => setWin(k)} data-testid={`fb-weak-win-${k}`}
            className={`rounded px-2 py-1 font-mono text-[10px] uppercase ${win === k ? "bg-orange-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:text-orange-300"}`}>
            {k === "prev" ? "prev season" : k === "index" ? "blended index" : k}
          </button>
        ))}
      </div>
      <p className="mb-2 font-mono text-[9px] text-slate-500">Cells: count of opponent first baskets · share of that team's opponent-first games. League: {POS.map((p) => `${p} ${pct(lg[p] || 0, 0)}`).join(" · ")} · {d.league.games} games.</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] font-mono text-[11px]">
          <thead><tr className="border-b border-slate-800 text-left text-[9px] uppercase text-slate-500"><th className="px-1.5 py-1">Team</th><th className="px-1.5">Games (opp first)</th>{POS.map((p) => <th key={p} className="px-1.5">{p}</th>)}</tr></thead>
          <tbody>
            {d.teams.map((t) => {
              const w = win === "index" ? null : t[win];
              return (
                <tr key={t.team} className="border-b border-slate-900" data-testid={`fb-weak-row-${t.abbr}`}>
                  <td className="px-1.5 py-1 font-bold text-slate-100">{t.abbr}</td>
                  <td className="px-1.5 text-slate-400">{win === "index" ? `${t.L20.games} (${t.L20.opp_first})` : `${w.games} (${w.opp_first})`}</td>
                  {POS.map((p) => {
                    const ix = t.index[p].index;
                    const hot = ix != null && ix >= 1.25;
                    return <td key={p} className={`px-1.5 ${hot ? "font-bold text-orange-300" : "text-slate-300"}`}>{win === "index" ? na(ix, "x") : cell(w, p)}</td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const OfficialCard = ({ date, onPlayer }) => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { let live = true; setD(null); axios.get(`${API}/card`, { params: { date } }).then((r) => live && setD(r.data)).catch(() => live && setD({ error: true })); return () => { live = false; }; }, [date]);
  if (!d) return <Loading />;
  if (d.error) return <p className="font-mono text-xs text-rose-400">Official card unavailable.</p>;
  return (
    <div className="space-y-3" data-testid="fb-card">
      <div className="flex flex-wrap gap-1.5">{Object.entries(d.counts).map(([k, v]) => <span key={k} data-testid={`fb-card-count-${k.replace(/\W+/g, "-").toLowerCase()}`}><StatusPill s={`${k} ${v}`} />{" "}</span>)}</div>
      <p className="font-mono text-[10px] text-slate-500">{d.rule}</p>
      {!d.cards.length && <p className="font-mono text-xs text-slate-500" data-testid="fb-card-empty">No NBA games on this date.</p>}
      {d.cards.map((g) => (
        <div key={g.game._id} className="rounded-xl border border-slate-800 bg-[#0B0F17] p-3" data-testid={`fb-card-game-${g.game._id}`}>
          <p className="font-display text-sm font-bold uppercase text-slate-100">{g.game.away.abbr} @ {g.game.home.abbr} <span className="font-mono text-[10px] font-normal text-slate-500">· {g.game.date.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")} {time(g.game.start)} · tip {g.tip.away_jumper || "N/A"} vs {g.tip.home_jumper || "N/A"}</span></p>
          {!g.rows.length && <p className="font-mono text-[10px] text-slate-600">Every candidate is PASS.</p>}
          {g.rows.map((c) => (
            <button key={c.id} type="button" onClick={() => onPlayer(c.id)} data-testid={`fb-card-row-${c.id}`} className="mt-1.5 grid w-full grid-cols-[auto_1fr_auto] items-start gap-2 rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-left transition-colors hover:border-orange-600/60">
              <StatusPill s={c.status} />
              <span className="min-w-0 font-mono text-[10px] text-slate-400">
                <b className="text-[12px] text-orange-100">{c.name}</b> · {c.team} {c.pos || "N/A"} · {c.starter === "CONFIRMED" ? "confirmed starter" : `${c.starter.toLowerCase()} starter`} · {c.archetypes[0]}<br />
                tip edge {pct(c.tip_edge, 0)} · opp weakness {na(c.weak_index, "x")} · history {na(c.fb_rate, "%")} · {c.starts} starts{c.reasons[0] ? ` · ${c.reasons[0]}` : ""}
                <span className="block text-slate-300" data-testid={`fb-card-reason-${c.id}`}>{c.status}: {c.status_reason}</span>
                {[...c.missing, ...c.warnings].length > 0 && <span className="block text-amber-300">⚠ {[...c.missing, ...c.warnings].join(" · ")}</span>}
              </span>
              <span className="text-right font-mono"><b className="block text-orange-300">{na(c.fusion)}</b><span className="text-[9px] text-slate-500">{c.prob_calibrated ? pct(c.prob) : "uncal."}</span></span>
            </button>
          ))}
          <p className="mt-1 font-mono text-[8px] text-slate-600">Last updated {new Date(g.updated).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
};

export const Loading = () => <div className="flex items-center gap-2 py-6 font-mono text-[11px] text-slate-500"><Loader2 className="h-4 w-4 animate-spin text-orange-400" /> Loading…</div>;
